/**
 * La lógica de la pantalla de conexiones, fuera del componente (SPEC-026).
 *
 * Vive aquí por el mismo motivo que `navegacion.ts`: las preguntas que importan
 * —"¿qué se le enseña a alguien cuyo ERP lleva tres días muerto?", "¿qué orden
 * tienen las filas?"— no se contestan mirando la pantalla con un caso, se
 * contestan con todos los casos a la vez, y eso solo se hace en una prueba.
 */

import type { Conexion } from "../api/tipos.ts";

/** Lo que pide la mirada de una persona. Nada más la pide. */
export const pideAtencion = (c: Conexion) => c.estado === "EN_SILENCIO";

/**
 * Lo que pide atención va primero; el resto conserva el orden de la API.
 *
 * Conservarlo importa: la API las devuelve vivas antes que revocadas y por
 * antigüedad, que es el orden de Configuración. Dos listas de lo mismo en
 * distinto orden obligan a buscar dos veces.
 */
export function ordenar(conexiones: Conexion[]): Conexion[] {
  return [...conexiones].sort((a, b) => Number(pideAtencion(b)) - Number(pideAtencion(a)));
}

/**
 * Una frase, no un estado técnico.
 *
 * `AL_DIA_CON_COLA` no significa nada para quien mira; "recogiendo pagos" sí. El
 * estado en crudo se queda en la insignia, para quien lo necesite al hablar con
 * soporte.
 */
export function descripcionDe(c: Conexion, horas: number): string {
  switch (c.estado) {
    case "REVOCADA":
      return "Revocada. Ya no puede entrar.";
    case "SIN_ESTRENAR":
      // Se distingue "existe y nunca ha entrado" de "entra pero no recoge
      // pagos". El segundo es un ERP que solo sube facturas, que está
      // trabajando; decirle a su dueño que está sin usar sería falso.
      return c.ultimoUso
        ? "Ha entrado, pero todavía no ha recogido ningún pago."
        : "Creada y sin usar todavía.";
    case "AL_DIA":
      return "Al día: no le queda nada por recoger.";
    case "AL_DIA_CON_COLA":
      return `Recogiendo: le faltan ${c.sinLeer}.`;
    case "EN_SILENCIO":
      return `Sin leer desde hace ${c.horasSinLeer} horas, con ${c.sinLeer} esperando (el aviso salta a las ${horas}).`;
  }
}

/**
 * Cuándo llamó por última vez, sea para leer o para cargar.
 *
 * Cae a la última lectura si no hay constancia de otra llamada. Sin esto, la
 * tarjeta llega a contradecirse —"última lectura hace 3 minutos" junto a "última
 * llamada: nunca"—, y una pantalla que se contradice a sí misma no se cree en
 * ninguna de sus dos mitades.
 *
 * Que `ultimoUso` falte teniendo lecturas no debería pasar en producción, porque
 * toda lectura pasa por la API. Pero "no debería pasar" no es una razón para
 * pintarlo mal cuando pasa.
 */
export const ultimaLlamada = (c: Conexion) => c.ultimoUso ?? c.ultimaLectura;
